import { useState, useMemo } from "react";
import { X, Plus, Minus } from "lucide-react";
import { BORDAS, SIZE_LABELS, SIZE_ORDER } from "@/data/menu";

const formatBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * Modal shown after user clicks "+ Adicionar" on a pizza card.
 * Lets user tweak size, optionally add a borda recheada and set qty.
 * Called with { item, initialSize, categoryId, isDoce }.
 */
export const BordaModal = ({ open, payload, onClose, onConfirm }) => {
  const [size, setSize] = useState(payload?.initialSize || "8f");
  const [bordaId, setBordaId] = useState(null);
  const [qty, setQty] = useState(1);

  // Reset internal state when a new payload comes in
  useMemo(() => {
    if (payload) {
      setSize(payload.initialSize || "8f");
      setBordaId(null);
      setQty(1);
    }
  }, [payload]);

  if (!open || !payload) return null;

  const { item, categoryId, isDoce, isHalfHalf, flavor2 } = payload;
  const basePrice = payload.basePrice ?? item.prices[size];
  const availableBordas = BORDAS.filter((b) => (isDoce ? b.kind === "doce" : b.kind === "tradicional"));
  const selectedBorda = BORDAS.find((b) => b.id === bordaId) || null;
  const bordaPrice = size === "broto" || !selectedBorda ? 0 : selectedBorda.prices[size] || 0;
  const unitPrice = basePrice + bordaPrice;
  const total = unitPrice * qty;

  const confirm = () => {
    onConfirm({
      item,
      categoryId,
      size,
      basePrice,
      borda: selectedBorda ? { id: selectedBorda.id, label: selectedBorda.label, price: bordaPrice } : null,
      price: unitPrice,
      qty,
      isHalfHalf: !!isHalfHalf,
      flavor2: flavor2 || null,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end" data-testid="borda-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md mx-auto bg-zinc-950 border-t border-zinc-800 rounded-t-3xl max-h-[92vh] flex flex-col shadow-2xl animate-fade-up">
        {/* Header */}
        <div className="flex items-start gap-3 p-4 border-b border-zinc-800">
          <div className="w-14 h-14 rounded-xl overflow-hidden bg-zinc-800 shrink-0">
            <img src={item.image} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-widest text-rose-400 font-semibold">
              {isHalfHalf ? "Meio a meio" : "Personalize seu pedido"}
            </p>
            <h3 className="font-display text-lg leading-tight truncate">
              {isHalfHalf ? `1/2 ${item.name} · 1/2 ${flavor2?.name}` : item.name}
            </h3>
            {isHalfHalf && (
              <p className="text-[11px] text-zinc-500 mt-0.5">Cobramos o valor do sabor mais caro.</p>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="borda-close-btn"
            className="w-9 h-9 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Size picker */}
          <section>
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
              Tamanho
            </p>
            <div className="grid grid-cols-4 gap-2" data-testid="borda-size-picker">
              {SIZE_ORDER.map((s) => {
                const priceAt = payload.getPriceForSize ? payload.getPriceForSize(s) : item.prices[s];
                return (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`borda-size-${s}`}
                    className={`text-xs font-semibold py-2 rounded-xl border transition-colors press ${
                      size === s
                        ? "bg-rose-600 border-rose-600 text-white"
                        : "bg-zinc-900 border-zinc-800 text-zinc-300"
                    }`}
                  >
                    <div>{SIZE_LABELS[s]}</div>
                    <div className="text-[10px] opacity-80 mt-0.5">{formatBRL(priceAt)}</div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Bordas */}
          <section>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
                Borda recheada <span className="normal-case text-zinc-600 tracking-normal">(opcional)</span>
              </p>
              {size === "broto" && (
                <span className="text-[10px] text-rose-400">Indisponível no Broto</span>
              )}
            </div>
            <div className="space-y-2" data-testid="borda-options">
              <button
                onClick={() => setBordaId(null)}
                data-testid="borda-nenhuma"
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border press ${
                  bordaId === null ? "bg-rose-600/15 border-rose-600 text-white" : "bg-zinc-900 border-zinc-800 text-zinc-300"
                }`}
              >
                <span className="text-sm font-semibold">Sem borda</span>
                <span className="text-[11px] text-zinc-500">Grátis</span>
              </button>
              {availableBordas.map((b) => {
                const bp = size === "broto" ? null : b.prices[size];
                const disabled = size === "broto";
                return (
                  <button
                    key={b.id}
                    onClick={() => !disabled && setBordaId(b.id)}
                    disabled={disabled}
                    data-testid={`borda-${b.id}`}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border press text-left ${
                      bordaId === b.id
                        ? "bg-rose-600/15 border-rose-600 text-white"
                        : "bg-zinc-900 border-zinc-800 text-zinc-300"
                    } ${disabled ? "opacity-40" : ""}`}
                  >
                    <span className="text-sm font-semibold pr-2">{b.label}</span>
                    <span className="text-[11px] text-rose-400 font-bold shrink-0">
                      {bp ? `+ ${formatBRL(bp)}` : "—"}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Qty */}
          <section className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
              Quantidade
            </p>
            <div className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-full px-2">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="borda-qty-dec"
                className="w-8 h-8 flex items-center justify-center"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-4 text-center text-sm font-bold" data-testid="borda-qty">{qty}</span>
              <button
                onClick={() => setQty((q) => q + 1)}
                data-testid="borda-qty-inc"
                className="w-8 h-8 flex items-center justify-center"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </section>
        </div>

        <div className="border-t border-zinc-800 p-4">
          <button
            onClick={confirm}
            data-testid="borda-confirm-btn"
            className="press w-full flex items-center justify-between bg-gradient-to-b from-rose-500 to-rose-600 text-white font-semibold px-5 py-3.5 rounded-2xl shadow-[0_10px_30px_-10px_rgba(244,63,94,0.7)]"
          >
            <span>Adicionar ao pedido</span>
            <span className="font-display text-lg" data-testid="borda-total">{formatBRL(total)}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
